import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
    FiCalendar,
    FiUser,
    FiBriefcase,
    FiClock,
    FiCheckCircle,
    FiXCircle,
    FiFileText,
    FiTrash2,
    FiEye,
    FiFilter,
    FiDownload,
    FiRefreshCw,
    FiChevronLeft,
    FiChevronRight,
    FiAlertCircle,
    FiTrendingUp,
    FiTrendingDown,
    FiArchive
} from "react-icons/fi";
import {
    MdOutlineGavel,
    MdOutlineCancel,
    MdOutlinePendingActions
} from "react-icons/md";
import { HiDocumentReport, HiDocumentRemove } from "react-icons/hi";
import "../styles/AdminAppointmentManagement.css";

export default function AdminAppointmentManagement() {
    const { token } = useAuth();

    const [activeTab, setActiveTab] = useState("INCOMPLETE");
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalAppointments: 0,
        incomplete: 0,
        filedInCourt: 0,
        dismissed: 0,
        rejected: 0
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({
        practiceArea: "",
        dateRange: ""
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    });
    const [exportLoading, setExportLoading] = useState(false);

    /* ======================
       FETCH APPOINTMENTS
    ====================== */
    const fetchAppointments = async () => {
        try {
            setLoading(true);

            const queryParams = new URLSearchParams({
                type: activeTab,
                page: pagination.page,
                limit: pagination.limit,
                ...filters,
                ...(searchTerm && { search: searchTerm })
            }).toString();

            const res = await fetch(
                `${API_BASE_URL}/api/admin/appointments?${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data = await res.json();
            if (data.success) {
                setAppointments(data.data);
                if (data.stats) {
                    setStats({
                        totalAppointments: data.stats.totalAppointments || 0,
                        incomplete: data.stats.incomplete || 0,
                        filedInCourt: data.stats.filedInCourt || 0,
                        dismissed: data.stats.dismissed || 0,
                        rejected: data.stats.rejected || 0
                    });
                }
                setPagination(data.pagination || {
                    page: 1,
                    limit: 10,
                    total: data.data.length,
                    pages: 1
                });
            } else {
                toast.error("Failed to load appointments");
            }
        } catch (error) {
            toast.error("Failed to load appointments");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, [activeTab, pagination.page, filters, searchTerm]);

    /* ======================
       EXPORT FUNCTIONALITY
    ====================== */
    const handleExport = async () => {
        try {
            setExportLoading(true);
            const params = new URLSearchParams({
                type: activeTab,
                ...filters,
                search: searchTerm,
                export: "excel"
            }).toString();

            const response = await fetch(
                `${API_BASE_URL}/api/admin/export-appointments?${params}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `appointments_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            toast.success("Excel downloaded successfully");
        } catch (error) {
            toast.error("Excel export failed");
        } finally {
            setExportLoading(false);
        }
    };

    /* ======================
       VIEW REJECTION REASON
    ====================== */
    const handleViewRejectionReason = (reason) => {
        Swal.fire({
            title: "Rejection Reason",
            html: `
        <div style="text-align:left; line-height:1.6">
          ${reason || "No rejection reason provided"}
        </div>
      `,
            icon: "info",
            confirmButtonText: "Close",
            confirmButtonColor: "#111827"
        });
    };

    /* ======================
       DELETE APPOINTMENT
    ====================== */
    const handleDelete = async (id, appointmentId) => {
        const result = await Swal.fire({
            title: "Delete Appointment?",
            html: `<p>Are you sure you want to delete appointment:</p>
             <strong>${appointmentId}</strong>
             <p class="delete-warning">This action cannot be undone!</p>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        try {
            await fetch(`${API_BASE_URL}/api/admin/appointments/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setAppointments(prev => prev.filter(a => a._id !== id));

            // Update stats
            setStats(prev => ({
                ...prev,
                totalAppointments: Math.max(0, prev.totalAppointments - 1)
            }));

            toast.success("Appointment deleted successfully");
        } catch (error) {
            toast.error("Delete failed");
        }
    };

    /* ======================
       FINAL STATUS LABEL
    ====================== */
    const getFinalStatus = (app) => {
        if (app.status === "REJECTED") return "Rejected";
        if (app.caseStatus === "FILED_IN_COURT") return "Filed in Court";
        if (app.caseStatus === "DISMISSED") return "Dismissed";
        if (app.appointmentCallStatus === "NOT_STARTED") return "Incomplete";
        return "-";
    };

    /* ======================
       RENDER STATUS BADGE
    ====================== */
    const renderStatusBadge = (app) => {
        const status = getFinalStatus(app);
        const statusConfig = {
            "Incomplete": { color: "#f59e0b", icon: <MdOutlinePendingActions /> },
            "Filed in Court": { color: "#10b981", icon: <FiCheckCircle /> },
            "Dismissed": { color: "#6b7280", icon: <FiArchive /> },
            "Rejected": { color: "#dc2626", icon: <FiXCircle /> }
        };

        const config = statusConfig[status] || { color: "#6b7280", icon: <FiAlertCircle /> };

        return (
            <span
                className="appointment-status-badge"
                style={{ backgroundColor: `${config.color}15`, color: config.color }}
                title={app.status === "REJECTED" ? app.rejectionReason : ""}
            >
                {config.icon}
                <span>{status}</span>
            </span>
        );
    };

    const getEndTime = (startDate, durationMinutes) => {
        const d = new Date(startDate);
        d.setMinutes(d.getMinutes() + (durationMinutes || 0));
        return d;
    };

    /* ======================
       HEADER STATS COMPONENT
    ====================== */
    const renderHeaderStats = () => (
        <div className="appointment-stats-grid">
            <div className="appointment-stat-card appointment-stat-blue">
                <div className="appointment-stat-icon">
                    <FiCalendar />
                </div>
                <div className="appointment-stat-content">
                    <h3 className="appointment-stat-value">{stats.totalAppointments}</h3>
                    <p className="appointment-stat-label">Total Appointments</p>
                </div>
            </div>

            <div className="appointment-stat-card appointment-stat-orange">
                <div className="appointment-stat-icon">
                    <MdOutlinePendingActions />
                </div>
                <div className="appointment-stat-content">
                    <h3 className="appointment-stat-value">{stats.incomplete}</h3>
                    <p className="appointment-stat-label">Incomplete</p>
                    <span className="appointment-stat-subtext">Pending Resolution</span>
                </div>
            </div>

            <div className="appointment-stat-card appointment-stat-green">
                <div className="appointment-stat-icon">
                    <HiDocumentReport />
                </div>
                <div className="appointment-stat-content">
                    <h3 className="appointment-stat-value">{stats.filedInCourt}</h3>
                    <p className="appointment-stat-label">Filed in Court</p>
                    <span className="appointment-stat-subtext">Successfully Filed</span>
                </div>
            </div>

            <div className="appointment-stat-card appointment-stat-red">
                <div className="appointment-stat-icon">
                    <MdOutlineCancel />
                </div>
                <div className="appointment-stat-content">
                    <h3 className="appointment-stat-value">{stats.rejected}</h3>
                    <p className="appointment-stat-label">Rejected</p>
                    <span className="appointment-stat-subtext">Not Accepted</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="premium-appointment-management">
            {/* ======================
         HEADER
      ====================== */}
            <div className="appointment-header">
                <div className="appointment-header-left">
                    <h1 className="appointment-page-title">Appointment Management</h1>
                    <p className="appointment-page-subtitle">
                        Monitor and manage all legal appointments
                    </p>
                </div>
                <div className="appointment-header-right">
                    <button className="appointment-refresh-btn" onClick={fetchAppointments}>
                        <FiRefreshCw size={18} />
                        <span>Refresh</span>
                    </button>
                    <button
                        className="appointment-export-btn"
                        onClick={handleExport}
                        disabled={exportLoading || appointments.length === 0}
                        title={appointments.length === 0 ? "No data to export" : "Export to Excel"}
                    >
                        {exportLoading ? (
                            <>
                                <div className="appointment-export-spinner"></div>
                                <span>Exporting...</span>
                            </>
                        ) : (
                            <>
                                <FiDownload size={18} />
                                <span style={{ color: "white" }}>Export ({appointments.length})</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* ======================
         STATS
      ====================== */}
            {renderHeaderStats()}

            {/* ======================
         MAIN CONTENT
      ====================== */}
            <div className="appointment-management-content">
                {/* Controls */}
                <div className="appointment-controls">
                    <div className="appointment-tabs-container">
                        <button
                            className={`appointment-tab-btn ${activeTab === "INCOMPLETE" ? "appointment-tab-active" : ""}`}
                            onClick={() => setActiveTab("INCOMPLETE")}
                        >
                            <MdOutlinePendingActions size={18} />
                            <span>Incomplete Appointments ({stats.incomplete})</span>
                        </button>

                        <button
                            className={`appointment-tab-btn ${activeTab.startsWith("COMPLETED") ? "appointment-tab-active" : ""}`}
                            onClick={() => setActiveTab("COMPLETED_FILED")}
                        >
                            <FiCheckCircle size={18} />
                            <span>Completed Appointments ({stats.filedInCourt + stats.dismissed + stats.rejected})</span>
                        </button>
                    </div>

                    <div className="appointment-controls-right">
                        <div className="appointment-search-box">
                            <FiEye className="appointment-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by ID, user, or solicitor..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="appointment-search-input"
                            />
                        </div>
                    </div>
                </div>

                {/* ======================
           COMPLETED SUB-TABS
        ====================== */}
                {activeTab.startsWith("COMPLETED") && (
                    <div className="appointment-subtabs-container">
                        <button
                            className={`appointment-subtab-btn ${activeTab === "COMPLETED_FILED" ? "appointment-subtab-active" : ""}`}
                            onClick={() => setActiveTab("COMPLETED_FILED")}
                        >
                            <HiDocumentReport size={16} />
                            <span>Filed in Court ({stats.filedInCourt})</span>
                        </button>

                        <button
                            className={`appointment-subtab-btn ${activeTab === "COMPLETED_DISMISSED" ? "appointment-subtab-active" : ""}`}
                            onClick={() => setActiveTab("COMPLETED_DISMISSED")}
                        >
                            <FiArchive size={16} />
                            <span>Dismissed ({stats.dismissed})</span>
                        </button>

                        <button
                            className={`appointment-subtab-btn ${activeTab === "COMPLETED_REJECTED" ? "appointment-subtab-active" : ""}`}
                            onClick={() => setActiveTab("COMPLETED_REJECTED")}
                        >
                            <MdOutlineCancel size={16} />
                            <span>Rejected ({stats.rejected})</span>
                        </button>
                    </div>
                )}

                {/* ======================
           TABLE
        ====================== */}
                <div className="appointment-table-container">
                    {loading ? (
                        <div className="appointment-loading-state">
                            <div className="appointment-loading-spinner"></div>
                            <p>Loading appointments...</p>
                        </div>
                    ) : (
                        <table className="appointment-data-table">
                            <thead>
                                <tr>
                                    <th>Appointment ID</th>
                                    <th>User Information</th>
                                    <th>Solicitor Information</th>
                                    <th>Practice Area</th>
                                    <th>Appointment Date</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {appointments.length > 0 ? (
                                    appointments.map(app => (
                                        <tr key={app._id} className="appointment-data-row">
                                            <td>
                                                <div className="appointment-id-cell">
                                                    {app.appointmentId}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="appointment-user-info">
                                                    <div className="appointment-user-avatar">
                                                        {app.userId?.userProfile?.photo ? (
                                                            <img
                                                                src={app.userId.userProfile.photo}
                                                                alt={app.userDetails?.name}
                                                                className="appointment-avatar-img"
                                                            />
                                                        ) : (
                                                            <div className="avatar-fallback">
                                                                {app.userDetails?.name?.charAt(0)?.toUpperCase() || "U"}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="appointment-user-details">
                                                        <div className="appointment-user-name">
                                                            {app.userDetails?.name || "-"}
                                                        </div>
                                                        <div className="appointment-user-meta">
                                                            <span className="appointment-user-email">
                                                                {app.userDetails?.email || "-"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="appointment-solicitor-info">
                                                    <div className="appointment-solicitor-avatar">
                                                        {app.solicitorId?.profile?.photo ? (
                                                            <img
                                                                src={app.solicitorId.profile.photo}
                                                                alt={app.solicitorDetails?.name}
                                                                className="appointment-avatar-img"
                                                            />
                                                        ) : (
                                                            <div className="avatar-fallback">
                                                                {app.solicitorDetails?.name?.charAt(0)?.toUpperCase() || "S"}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="appointment-solicitor-details">
                                                        <div className="appointment-solicitor-name">
                                                            {app.solicitorDetails?.name || "-"}
                                                        </div>
                                                        <div className="appointment-solicitor-meta">
                                                            <span className="appointment-solicitor-id">
                                                                {app.solicitorDetails?.solicitorId || "No ID"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <span className="appointment-practice-area">
                                                    <FiBriefcase size={14} />
                                                    {app.practiceArea}
                                                </span>
                                            </td>

                                            <td>
                                                <div className="appointment-date-cell">
                                                    {new Date(app.appointmentDate).toLocaleDateString()}
                                                    <div className="appointment-time">
                                                        {new Date(app.appointmentDate).toLocaleTimeString([], {
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                {renderStatusBadge(app)}
                                            </td>

                                            <td>
                                                <div className="appointment-action-buttons">
                                                    {app.status === "REJECTED" && (
                                                        <button
                                                            className="appointment-view-btn"
                                                            onClick={() => handleViewRejectionReason(app.rejectionReason)}
                                                            title="View Rejection Reason"
                                                        >
                                                            <FiEye size={16} />
                                                            <span>View Reason</span>
                                                        </button>
                                                    )}

                                                    <button
                                                        className="appointment-delete-btn"
                                                        onClick={() => handleDelete(app._id, app.appointmentId)}
                                                        title="Delete Appointment"
                                                    >
                                                        <FiTrash2 size={16} />
                                                        <span>Delete</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr className="appointment-no-data-row">
                                        <td colSpan="7">
                                            <div className="appointment-no-data">
                                                <FiCalendar size={48} />
                                                <p>No appointments found</p>
                                                <small>Try adjusting your search or filters</small>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                        <div className="appointment-pagination">
                            <button
                                className="appointment-pagination-btn"
                                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                                disabled={pagination.page === 1}
                            >
                                <FiChevronLeft size={18} />
                                <span>Previous</span>
                            </button>

                            <div className="appointment-page-numbers">
                                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                    let pageNum;
                                    if (pagination.pages <= 5) {
                                        pageNum = i + 1;
                                    } else if (pagination.page <= 3) {
                                        pageNum = i + 1;
                                    } else if (pagination.page >= pagination.pages - 2) {
                                        pageNum = pagination.pages - 4 + i;
                                    } else {
                                        pageNum = pagination.page - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            className={`appointment-page-btn ${pagination.page === pageNum ? "appointment-page-active" : ""}`}
                                            onClick={() => setPagination({ ...pagination, page: pageNum })}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                className="appointment-pagination-btn"
                                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                                disabled={pagination.page === pagination.pages}
                            >
                                <span>Next</span>
                                <FiChevronRight size={18} />
                            </button>

                            <div className="appointment-page-info">
                                Showing {(pagination.page - 1) * pagination.limit + 1}-
                                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}